import { sleep } from '@cp/utils/sleep';
import { ClientProfileUpdateRequest } from '@common/model/client/profile/clientProfileUpdateRequest';
import { authenticationSlice, coreApi } from '@cp/shared/api/core';
import { ClientProfileViewModel } from '@common/model/client';
import { ApiServerEntityIDType } from '@cp/utils/apiServer/types';
import { ClientProfileUpdateResponse } from '@common/model/client/profile/clientProfileUpdateResponse';
import { ApiRouteBody } from '@common/api/routeBody';
import { ApiRoute } from '@common/api/route';
import { generateApiUrl } from '@common/api/configure';
import { apiRouteMethodDict } from '@common/api/routeMethod';
import { CatalogItemResponseClientOwnViewModel } from '@common/model/catalog/item/response';
import { CatalogItemViewClientOwnViewModel } from '@common/model/catalog/item/catalogItemView';
import { CoreLocationApiTags, coreLocationApiTagTypes } from '@cp/entities/location';
import { CoreClientApiTags, coreClientApiTagTypes } from './tags';

export const coreCurrentClientApi = coreApi
  .enhanceEndpoints({ addTagTypes: coreClientApiTagTypes })
  .enhanceEndpoints({
    addTagTypes: coreLocationApiTagTypes,
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getProfile: builder.query<ClientProfileViewModel, void>({
        query: () => ({
          url: 'client/profile',
        }),
        providesTags: [CoreClientApiTags.ClientProfile],
      }),

      updateProfile: builder.mutation<ClientProfileUpdateResponse, ClientProfileUpdateRequest>({
        query: (body) => ({
          method: 'PUT',
          url: 'client/profile',
          body,
        }),
        invalidatesTags: (res, b, r) => {
          if (!res?.isClientProfileUpdated) {
            return [];
          }
          if (r.cityId) {
            return [{ type: CoreLocationApiTags.City, id: r.cityId }];
          }
          return [];
        },
        onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
          try {
            const { data } = await queryFulfilled;
            if (!data.isClientProfileUpdated) {
              return;
            }
            dispatch(
              coreCurrentClientApi.util.updateQueryData('getProfile', undefined, () => {
                return data.clientProfile;
              }),
            );
          } catch {}
        },
      }),

      otpCompleteChallenge: builder.mutation<void, { id: string; body: ApiRouteBody[ApiRoute.ClientProfileOTPSCompleteChallenge] }>({
        query: ({ id, body }) => ({
          url: generateApiUrl(ApiRoute.ClientProfileOTPSCompleteChallenge, { id }),
          method: apiRouteMethodDict[ApiRoute.ClientProfileOTPSCompleteChallenge],
          body,
        }),
        invalidatesTags: () => {
          return [CoreClientApiTags.ClientProfile];
        },
      }),

      deleteProfile: builder.mutation<boolean, void>({
        query: () => ({
          method: 'DELETE',
          url: 'client',
        }),
        onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
          try {
            await queryFulfilled;
            await sleep(0);
            dispatch(coreCurrentClientApi.util.resetApiState());
            dispatch(authenticationSlice.actions.setToken(null));
          } catch {}
        },
      }),

      deleteProfilePhoto: builder.mutation<void, { id: ApiServerEntityIDType }>({
        query: (params) => ({
          method: 'DELETE',
          url: `client/photos/${params.id}`,
        }),
        onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
          try {
            await queryFulfilled;
            await sleep(0);
            dispatch(
              coreCurrentClientApi.util.updateQueryData('getProfile', undefined, (draft) => {
                draft.mainPhoto = undefined;
              }),
            );
          } catch {}
        },
      }),

      getOwnCatalogItemResponses: builder.query<CatalogItemResponseClientOwnViewModel[], void>({
        query: () => ({
          url: generateApiUrl(ApiRoute.ClientCatalogItemResponsesGet),
          method: apiRouteMethodDict[ApiRoute.ClientCatalogItemResponsesGet],
        }),
        providesTags: [CoreClientApiTags.ClientOwnCatalogItemResponses],
      }),

      getOwnCatalogItemViews: builder.query<CatalogItemViewClientOwnViewModel[], void>({
        query: () => ({
          url: generateApiUrl(ApiRoute.ClientCatalogItemViewsGet),
          method: apiRouteMethodDict[ApiRoute.ClientCatalogItemViewsGet],
        }),
        providesTags: [CoreClientApiTags.ClientOwnCatalogItemViews],
      }),
    }),

    overrideExisting: false,
  });
